import React, { useState } from 'react';
import { useAppKitAccount } from '@reown/appkit/react';
import { useWriteContract } from 'wagmi';
import { useT } from '../../i18n';
import { chainId, erc20Address, cranniAddress } from '../../.config';

// Public BSC testnet only: the test USDT and CRANNI have an open mint, so anyone can top up and try every game.
const TESTNET_CHAIN = 97;
const FAUCET_URL = 'https://www.bnbchain.org/en/testnet-faucet';
const HIDE_KEY = 'lb-testnet-bar-hidden';
const mintAbi = [
  {
    type: 'function',
    name: 'mint',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [],
  },
];
const DRIPS = {
  usdt: { address: erc20Address, amount: 1_000n * 10n ** 6n },
  cranni: { address: cranniAddress, amount: 10_000n * 10n ** 8n },
};

const readHidden = () => {
  try {
    return sessionStorage.getItem(HIDE_KEY) === '1';
  } catch (err) {
    return false;
  }
};

export default function TestnetBar({ appKit }) {
  const t = useT();
  const { address, isConnected } = useAppKitAccount();
  const { writeContractAsync } = useWriteContract();
  const [hidden, setHidden] = useState(readHidden);
  const [busy, setBusy] = useState('');
  const [status, setStatus] = useState('');

  if (chainId !== TESTNET_CHAIN || hidden) return null;

  const drip = async (key) => {
    if (!isConnected) {
      appKit?.open();
      return;
    }
    setBusy(key);
    setStatus('');
    try {
      const { address: token, amount } = DRIPS[key];
      await writeContractAsync({ address: token, abi: mintAbi, functionName: 'mint', args: [address, amount] });
      setStatus(t('testnet.sent'));
    } catch (err) {
      setStatus(t('testnet.failed'));
    } finally {
      setBusy('');
    }
  };

  const hide = () => {
    try {
      sessionStorage.setItem(HIDE_KEY, '1');
    } catch (err) {
      // private mode: the bar comes back on the next page load
    }
    setHidden(true);
  };

  const button =
    'rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white transition hover:border-amber-300/60 hover:bg-white/10 disabled:opacity-50';

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t border-amber-300/30 bg-[#150c1e]/95 backdrop-blur">
      <div className="mx-auto flex max-w-screen-2xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 sm:px-6">
        <span className="rounded-full bg-amber-300 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-[#150c1e]">
          {t('testnet.label')}
        </span>
        <span className="text-xs text-white/70">{isConnected ? t('testnet.note') : t('testnet.connect')}</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <a className={button} href={FAUCET_URL} target="_blank" rel="noopener noreferrer">
            {t('testnet.gas')}
          </a>
          <button type="button" className={button} disabled={!!busy} onClick={() => drip('usdt')}>
            {busy === 'usdt' ? '…' : t('testnet.usdt')}
          </button>
          <button type="button" className={button} disabled={!!busy} onClick={() => drip('cranni')}>
            {busy === 'cranni' ? '…' : t('testnet.cranni')}
          </button>
          {status && <span className="text-xs text-amber-200">{status}</span>}
          <button type="button" className="px-1 text-xs text-white/50 hover:text-white" onClick={hide} aria-label={t('testnet.hide')}>
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
